import { fetchWithAuth } from './apiHelpers';

const normalizeItems = (response) => (Array.isArray(response?.items) ? response.items : []);

export async function listEnvVarSets(token) {
  return normalizeItems(await fetchWithAuth(token, '/saved-secrets/env-var-sets'));
}

export async function listBrokerCredentials(token) {
  return normalizeItems(await fetchWithAuth(token, '/saved-secrets/broker-credentials'));
}

export async function listBrokerSnapshots(token, options = {}) {
  const query = options.includeLatest ? '?includeLatest=true' : '';
  const response = await fetchWithAuth(token, `/broker-snapshots${query}`);
  return {
    items: normalizeItems(response),
    enabledForUser: response?.enabledForUser === true,
  };
}

export async function getBrokerSnapshotDetail(token, brokerCredentialId) {
  return fetchWithAuth(
    token,
    `/broker-snapshots/broker-credentials/${encodeURIComponent(brokerCredentialId)}/detail`
  );
}

export async function createBrokerCredential(token, payload) {
  return fetchWithAuth(token, '/saved-secrets/broker-credentials', {
    method: 'POST',
    body: payload,
  });
}

export async function createSavedBrokerCredentialFromOAuth(token, params) {
  const {
    broker,
    displayName,
    paperOrLive = 'live',
    externalAccountId,
    externalAccountLabel,
    providerMetadata,
    credentialValues,
    replaceExisting = true,
  } = params || {};
  const brokerLabel = typeof broker === 'string' && broker.trim()
    ? broker.trim().charAt(0).toUpperCase() + broker.trim().slice(1)
    : 'Broker';
  const modeLabel = paperOrLive === 'paper' ? 'paper' : 'live';
  const safeDisplayName = typeof displayName === 'string' && displayName.trim()
    ? displayName.trim()
    : `${brokerLabel} ${modeLabel} account`;

  return createBrokerCredential(token, {
    broker,
    displayName: safeDisplayName,
    paperOrLive,
    authMethod: 'oauth',
    externalAccountId,
    externalAccountLabel,
    providerMetadata: {
      oauthProvider: broker,
      connectionSource: 'broker_connection_oauth',
      ...(providerMetadata || {}),
    },
    credentialValues,
    replaceExisting,
  });
}

export async function startBrokerOAuth(token, provider, payload = {}) {
  return fetchWithAuth(token, `/broker_oauth/${provider}/start`, {
    method: 'POST',
    body: payload,
  });
}

export async function updateBrokerCredential(token, id, payload) {
  return fetchWithAuth(token, `/saved-secrets/broker-credentials/${id}`, {
    method: 'PATCH',
    body: payload,
  });
}

export async function deleteBrokerCredential(token, id) {
  return fetchWithAuth(token, `/saved-secrets/broker-credentials/${id}`, {
    method: 'DELETE',
  });
}

export async function createEnvVarSet(token, payload) {
  return fetchWithAuth(token, '/saved-secrets/env-var-sets', {
    method: 'POST',
    body: payload,
  });
}

export async function updateEnvVarSet(token, id, payload) {
  return fetchWithAuth(token, `/saved-secrets/env-var-sets/${id}`, {
    method: 'PATCH',
    body: payload,
  });
}

export async function deleteEnvVarSet(token, id) {
  return fetchWithAuth(token, `/saved-secrets/env-var-sets/${id}`, {
    method: 'DELETE',
  });
}

function buildRuntimePreferenceQuery(params = {}) {
  const search = new URLSearchParams();
  ['strategyId', 'aiStrategyRevisionId', 'linkedStrategyId'].forEach((key) => {
    const value = params[key];
    if (typeof value === 'string' && value.trim()) {
      search.set(key, value.trim());
    }
  });
  return search.toString();
}

export async function getRuntimePreference(token, params) {
  const query = buildRuntimePreferenceQuery(params);
  const response = await fetchWithAuth(
    token,
    `/saved-secrets/runtime-preferences${query ? `?${query}` : ''}`
  );
  return response?.item || null;
}

export async function updateRuntimePreference(token, payload) {
  return fetchWithAuth(token, '/saved-secrets/runtime-preferences', {
    method: 'PUT',
    body: payload,
  });
}
